import { useCallback, useEffect, useState } from "react";
import {
  computeFormBadge,
  computeHomeWeekly,
  DEFAULT_UNITS_PREFERENCE,
  Profile,
  recentPrs,
  SessionResult,
  hardSetsOfResult,
  startedAtMsOf,
  type FormBadge,
  type FormSession,
  type HomeWeekly,
  type PRsByExercise,
  type RecentPr,
  type UnitsPreference,
} from "@heraion/shared";

import { apiFetch } from "./api";
import { webStorage } from "./webStorage";

// localStorage SWR cache (see the hook comment). Versioned key so a shape
// change invalidates cleanly; best-effort (private mode / quota → no-op).
const CACHE_KEY = "jl.home.signals.v3";

type CachedSignals = {
  formBadge: FormBadge | null;
  weekly: HomeWeekly | null;
  prs: RecentPr[];
  units: UnitsPreference;
  bodyweightKg: number | null;
};

function readCache(): CachedSignals | null {
  try {
    const raw = webStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachedSignals) : null;
  } catch {
    return null;
  }
}

function writeCache(value: CachedSignals): void {
  try {
    webStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // best-effort
  }
}

// Lean Home-data fetch for the web-rnw Plan surface: the readiness badge (from
// the same /api/session-results page Progress reads), the recent-PRs strip
// (/api/me/records), and the profile (units + current bodyweight, for the
// PRs strip + quick weigh-in). Mobile has its own cached hooks; web only needs
// a one-shot read, so this stays local. The derivations are the shared ones,
// so the numbers match mobile. refreshProfile re-reads after a weigh-in.
// `weekStartDay` comes from the CALLER's own useProfileDoc() (PlanScreen.tsx
// already mounts it for computeWeeklyTargets): this hook does not fetch its
// own copy for it, to keep one profile-driven weekStartDay per screen.
// Undefined while that doc hasn't loaded yet ⇒ `weekly` stays null (no
// silent Monday default).
export function useHomeSignals(weekStartDay: number | undefined): {
  formBadge: FormBadge | null;
  weekly: HomeWeekly | null;
  prs: RecentPr[];
  units: UnitsPreference;
  bodyweightKg: number | null;
  refreshProfile: () => Promise<void>;
} {
  // Stale-while-revalidate cache (mobile reads records cache-first too;
  // /api/me/records recomputes PRs from full history, so a cold fetch is
  // ~seconds). Paint the last good signals from localStorage immediately,
  // then refresh underneath — so a page reload (the test loop) is instant.
  const [cache] = useState(readCache);
  const [formBadge, setFormBadge] = useState<FormBadge | null>(
    cache?.formBadge ?? null,
  );
  const [weekly, setWeekly] = useState<HomeWeekly | null>(
    cache?.weekly ?? null,
  );
  const [prs, setPrs] = useState<RecentPr[]>(cache?.prs ?? []);
  const [units, setUnits] = useState<UnitsPreference>(
    cache?.units ?? DEFAULT_UNITS_PREFERENCE,
  );
  const [bodyweightKg, setBodyweightKg] = useState<number | null>(
    cache?.bodyweightKg ?? null,
  );
  // Raw sessions, kept apart from `weekly` (below) so it recomputes on the
  // calendar-week boundary once the caller's weekStartDay lands, whichever
  // of the two independent inputs arrives second.
  const [sessions, setSessions] = useState<FormSession[] | null>(null);

  // Persist the latest signals so the next mount paints instantly.
  useEffect(() => {
    writeCache({ formBadge, weekly, prs, units, bodyweightKg });
  }, [formBadge, weekly, prs, units, bodyweightKg]);

  // TODO(cleanup): this /api/profiles fetch duplicates the caller's useProfileDoc().
  const refreshProfile = useCallback(async (): Promise<void> => {
    try {
      const raw = await apiFetch<unknown>("/api/profiles");
      const parsed = Profile.safeParse(raw);
      if (parsed.success) {
        setUnits(parsed.data.unitsPreference);
        setBodyweightKg(parsed.data.bodyweightKg ?? null);
      }
    } catch {
      // keep defaults (kg / unknown)
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // The two reads are independent — run them in parallel so the PRs strip
    // (records) doesn't wait on the heavier session-results page.
    const loadBadge = async () => {
      try {
        const resp = await apiFetch<{ items?: unknown[] }>(
          "/api/session-results?limit=200",
        );
        const loaded: FormSession[] = [];
        for (const raw of resp.items ?? []) {
          const parsed = SessionResult.safeParse(raw);
          if (!parsed.success) continue;
          loaded.push({
            startedAtMs: startedAtMsOf(raw, parsed.data),
            volumeKg: parsed.data.totalVolumeKg,
            // §8ter/`HM-3` — l'asse dello stato è la SERIE DURA, non il
            // tonnellaggio. ⛔ Era già in `parsed.data` e veniva scartato qui.
            hardSets: hardSetsOfResult(parsed.data),
          });
        }
        if (!cancelled) {
          setFormBadge(computeFormBadge(loaded, Date.now()));
          setSessions(loaded);
        }
      } catch {
        // leave the badge + weekly null — the cards hide themselves
      }
    };
    const loadPrs = async () => {
      try {
        const rec = await apiFetch<{ records?: PRsByExercise }>(
          "/api/me/records",
        );
        if (!cancelled) setPrs(recentPrs(rec.records ?? {}, Date.now()));
      } catch {
        // leave prs empty — the strip hides itself
      }
    };
    void loadBadge();
    void loadPrs();
    void refreshProfile();
    return () => {
      cancelled = true;
    };
  }, [refreshProfile]);

  // Calendar-week stats, recomputed whenever the sessions fetch or the
  // caller's weekStartDay lands, whichever comes second. weekStartDay stays
  // undefined until the caller's profile doc loads, so no default Monday
  // window gets computed on a guess.
  useEffect(() => {
    if (sessions === null || weekStartDay === undefined) {
      setWeekly(null);
      return;
    }
    setWeekly(computeHomeWeekly(sessions, Date.now(), weekStartDay));
  }, [sessions, weekStartDay]);

  return { formBadge, weekly, prs, units, bodyweightKg, refreshProfile };
}
